import type { FC } from 'react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { CalendarIcon, LocationIcon } from '@/assets';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationSurface } from '@/navigation/components/NavigationSurface';
import { AppRoute } from '@/navigation/types/navigation';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import { dayjs } from '@/time';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    surface: {
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    content: { flexDirection: 'row', alignItems: 'center' },
    date: {
      width: 80,
      minHeight: 80,
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.errorContainer
    },
    dateLabel: { color: theme.colors.onPrimary, textAlign: 'center' },
    text: { flex: 1, padding: 16, gap: 6 },
    tags: { gap: 8, flexDirection: 'row', alignItems: 'center' },
    tag: { alignItems: 'center', flexDirection: 'row', gap: 4 },
    tagLabel: { color: theme.colors.onSurfaceVariant }
  });

const HappeningCardComponent: FC<GenericPostDetail> = ({
  postId,
  allDayEvent,
  startTime,
  endTime,
  title,
  text,
  customAddress
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'components.happening'
  });

  const isSameDay =
    isEmptyOrNull(startTime) ||
    startTime === 0 ||
    isEmptyOrNull(endTime) ||
    endTime === 0 ||
    dayjs(startTime).isSame(dayjs(endTime), 'day');

  const durationLabel = useMemo(() => {
    const numberOfDays = isSameDay
      ? 0
      : dayjs(endTime).diff(dayjs(startTime), 'day');

    if (numberOfDays === 0 && allDayEvent) return t('fullDay');
    else if (numberOfDays === 0 && !allDayEvent)
      return `${dayjs(startTime).format('HH:mm')} - ${dayjs(endTime).format('HH:mm')}`;
    // add 1 because time differences is only 1d (24h) for a 2 day duration as the time component is 0:00
    else return t('multiDay', { days: numberOfDays + 1 });
  }, [allDayEvent, endTime, isSameDay, startTime, t]);

  const iconProps = { fill: theme.colors.error, width: 16, height: 16 };

  return (
    <NavigationSurface
      screen={AppRoute.PostDetail}
      params={{ id: postId }}
      style={styles.surface}
    >
      <View style={styles.content}>
        <View style={styles.date}>
          <Text variant="bodyLarge" style={styles.dateLabel}>
            {dayjs(startTime)
              .format('MMM')
              .replace('.', '')
              .toLocaleUpperCase()}
          </Text>

          <Text variant="headlineMedium" style={styles.dateLabel}>
            {dayjs(startTime).format('DD')}
          </Text>
        </View>

        <View style={styles.text}>
          <Text variant="bodyLarge" numberOfLines={1} ellipsizeMode="tail">
            {title ?? text}
          </Text>
          <View style={styles.tags}>
            <View style={[styles.tag, { flexShrink: 1 }]}>
              <CalendarIcon {...iconProps} />
              <Text variant="labelMedium" style={styles.tagLabel}>
                {durationLabel}
              </Text>
            </View>
            {!isEmptyOrNull(customAddress?.name) && (
              <View style={[styles.tag, { flex: 1 }]}>
                <LocationIcon {...iconProps} />
                <Text
                  variant="labelMedium"
                  style={styles.tagLabel}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {customAddress?.name}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </NavigationSurface>
  );
};

export const HappeningCard = memo(HappeningCardComponent);
HappeningCard.displayName = 'HappeningCard';
